import React from "react";
import { Box, Button, Hidden, makeStyles, Typography, Tooltip, useTheme, useMediaQuery } from "@material-ui/core";

import useCart from "../../hooks/useCart";
import { pushAddProductToCart, pushRemovedFromCart } from "lib/gtm";
import useScript2 from "hooks/useScript2";
import InfoOutline from "components/repairFunnel/assets/infoOutline.svg";
import moment from "moment";
import { formatDate } from "helpers/formatDate";
import InformationDrawer from "../design-system/molecules/InformationDrawer/InformationDrawer";
import IconCheap from "./assets/IconCheap.svg";
import AlmaIcon from "public/reconditionne/homepage/reinsurance/alma_header.svg";
const ALMA_SCRIPT_SRC = "https://unpkg.com/@alma/fragments@1.x/dist/alma-fragments.umd.js";

const AlmaText = ({ classes, paymentPlan }) => {
  if (!paymentPlan) {
    return null;
  }
  return (
    <Typography variant="body2" color="secondary">
      Avec notre partenaire <b>Alma</b> payez :
      <ul className={classes.ul}>
        {paymentPlan?.map((plan, index) => {
          return (
            <li key={index} className={classes.li}>
              {(plan.purchase_amount / 100).toString().replace(".", ",")}€
              {index === 0 ? ` aujourd'hui` : ` le ${formatDate(moment.unix(parseInt(plan.due_date)))}`}
              {plan.customer_fee ? (
                <Typography variant="caption" color="secondary">{` (dont ${(plan.customer_fee / 100)
                  .toString()
                  .replace(".", ",")}€ de frais)`}</Typography>
              ) : null}
            </li>
          );
        })}
      </ul>
    </Typography>
  );
};

export default function AddToCart({ product }) {
  const [cart, dispatch] = useCart();
  const classes = useStyle();
  const [almaClient, setAlmaClient] = React.useState(undefined);
  const [paymentPlan, setPaymentPlan] = React.useState(undefined);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const add = () => {
    pushAddProductToCart(product);
    dispatch({
      type: "add",
      product,
    });
  };

  const remove = (product) => {
    pushRemovedFromCart(product, "product_page");
    dispatch({
      type: "remove",
      product,
    });
  };

  const scriptStatus = useScript2(ALMA_SCRIPT_SRC);

  React.useEffect(() => {
    // Wait for alma client to be initialized and pass it to state
    if (scriptStatus === "ready") {
      //eslint-disable-next-line
      // @ts-ignore
      setAlmaClient(Alma);
    }
    if (almaClient && !paymentPlan) {
      const fragments = new almaClient.Fragments("11uG6yd6XpZua5SBSS7LENlzYDEze2hNfc", {
        mode: almaClient.ApiMode.TEST,
      });
      const paymentData = {
        payment: {
          purchase_amount: product.price,
          installments_count: 3,
        },
      };
      const paymentForm = fragments.createPaymentForm(paymentData);
      paymentForm.context.payment.then((payment) => {
        setPaymentPlan(payment.payment_plan);
      });
    }
  }, [cart, scriptStatus, almaClient, paymentPlan, product]);

  const alreadyInCart = cart?.selection.some((p) => p.product.slug === product.slug) || false;

  return (
    <Box display={isMobile ? "" : "flex"} alignItems={"center"}>
      <Hidden smDown implementation="css">
        <Box display="flex" alignItems="center">
          <Box display="block">
            <Box className={classes.textBox}>
              <Typography variant="body1" color="secondary" className={classes.text}>
                <Box>Payez en 3 fois</Box>
              </Typography>
              <Box ml={0.5} mt={1} width={64}>
                <AlmaIcon />
              </Box>
            </Box>
          </Box>
          {paymentPlan && (
            <Tooltip
              arrow
              placement="bottom"
              classes={{
                tooltip: classes.tooltip,
                arrow: classes.arrow,
              }}
              title={<AlmaText classes={classes} paymentPlan={paymentPlan} />}
            >
              <Button disableRipple className={classes.helper}>
                <InfoOutline />
              </Button>
            </Tooltip>
          )}
        </Box>
      </Hidden>
      <Hidden mdUp implementation="css">
        <Box display={"block"}>
          <InformationDrawer
            anchor="bottom"
            width="100%"
            title="Payez en 3 fois"
            button={
              <Box>
                <Box
                  p={3}
                  my={3}
                  className={classes.installmentsBox}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Box display="flex" alignItems="center">
                    <Box>
                      <Typography variant="body2" color="secondary" className={classes.text}>
                        Payez en 3 fois
                      </Typography>
                      <Box ml={0.5} mt={1} width={64}>
                        <AlmaIcon />
                      </Box>
                    </Box>
                  </Box>
                  <Typography variant="body2" color="secondary">
                    <b>En savoir plus</b>
                  </Typography>
                </Box>
              </Box>
            }
          >
            {() => (
              <>
                <Box textAlign="center" p={3}>
                  <IconCheap />
                </Box>
                <AlmaText classes={classes} paymentPlan={paymentPlan} />
              </>
            )}
          </InformationDrawer>
        </Box>
      </Hidden>
      {alreadyInCart ? (
        <Button
          variant="contained"
          color="primary"
          className={classes.button}
          fullWidth
          onClick={() => remove(product)}
        >
          ✓ Retirer du panier
        </Button>
      ) : (
        <Button
          variant="contained"
          color="primary"
          className={classes.button}
          fullWidth
          onClick={add}
          disabled={!product.available}
        >
          Passer commande
        </Button>
      )}
    </Box>
  );
}

const useStyle = makeStyles((theme) => ({
  installmentsBox: {
    borderTop: `1px solid #DDD`,
    borderBottom: `1px solid #DDD`,
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
  ul: {
    padding: `0 0 0 ${theme.spacing(2)}px `,
  },
  li: {
    [theme.breakpoints.down("md")]: {
      paddingTop: theme.spacing(2),
    },
  },
  tooltip: {
    padding: theme.spacing(2),
    marginLeft: theme.spacing(1),
    backgroundColor: theme.palette.common.white,
    color: theme.palette.text.secondary,
    filter: "drop-shadow(0px 4px 16px rgba(0, 0, 0, 0.1))",
    borderRadius: 12,
  },
  arrow: {
    color: theme.palette.common.white,
  },
  helper: {
    minWidth: "auto",
    height: "auto",
    padding: 0,
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(3),
  },
  button: {
    marginLeft: theme.spacing(2),
    [theme.breakpoints.down("md")]: {
      marginLeft: 0,
    },
  },
  textBox: {
    borderLeft: `4px solid ${theme.palette.secondary.main}`,
  },
  text: {
    whiteSpace: "nowrap",
    padding: `10px 10px 0 10px`,
    fontSize: 16,
  },
}));
